/**
 * TabErrorBoundary — Error boundary for individual tab panels
 *
 * Catches render errors in tab content and shows a recovery UI
 * without crashing the entire detail panel.
 */

import { Component, type ReactNode } from 'react';
import { Alert, Button, Stack, Text, Code } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

interface TabErrorBoundaryProps {
  /** Tab label for error messaging */
  tabName: string;
  children: ReactNode;
}

interface TabErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class TabErrorBoundary extends Component<TabErrorBoundaryProps, TabErrorBoundaryState> {
  constructor(props: TabErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): TabErrorBoundaryState {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Alert
          color="red"
          title={`${this.props.tabName} Error`}
          icon={<IconAlertTriangle size={16} />}
        >
          <Stack gap="xs">
            <Text size="sm">
              An error occurred while rendering the {this.props.tabName} tab.
            </Text>
            {this.state.error && (
              <Code block>{this.state.error.message}</Code>
            )}
            <Button
              size="xs"
              variant="outline"
              color="red"
              onClick={this.handleReset}
            >
              Retry
            </Button>
          </Stack>
        </Alert>
      );
    }

    return this.props.children;
  }
}
